import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { useMediaQuery } from '@/shared/lib';
import { cn } from '@/shared/utils';
import {
  MARKET_ASSETS_QTY,
  MARKET_TABS,
  type MarketTab,
} from '../config/marketTabs';
import { useMarketAssets } from '../model';
import { MarketAssetRow } from './MarketAssetRow';
import { MarketTabs } from './MarketTabs';
import { MarketSectionSkeleton } from './MarketSectionSkeleton';

const LIST_FADE_CLASSNAME =
  'tablet:-mx-[0.4rem] tablet:px-[0.4rem] tablet:max-h-[26.4rem] tablet:overflow-hidden tablet:mask-b-from-[calc(100%-2.4rem)]';

const MarketSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');

  const [activeTab, setActiveTab] = useState<MarketTab>(MARKET_TABS.popular);

  const { assets, baseAsset, isLoading } = useMarketAssets(activeTab);

  if (isLoading) {
    return <MarketSectionSkeleton />;
  }

  return (
    <div className="flex flex-col gap-[0.8rem]">
      <MarketTabs value={activeTab} onValueChange={setActiveTab} />

      <div className="flex flex-col">
        {assets.length === 0 ? (
          <span className="medium5 text-secondary py-[1.6rem]">
            {t('personalAccount.overview.market.empty')}
          </span>
        ) : (
          <div
            className={cn(
              'flex flex-col',
              assets.length >= MARKET_ASSETS_QTY && LIST_FADE_CLASSNAME
            )}>
            {assets.map(asset => (
              <MarketAssetRow
                key={asset.code}
                asset={asset}
                baseAsset={baseAsset}
                withSparkline={isTabletUp}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate('/markets')}
          className="flex h-[3.2rem] w-fit items-center gap-[0.4rem] mt-[1.2rem] pl-[1.2rem] pr-[0.8rem] rounded-[1rem] border border-stroke bg-background headline5 text-primary cursor-pointer hover:bg-secondary-bg transition-colors">
          {t('personalAccount.overview.market.allAssets')}
          <ArrowRightIcon className="size-[1.6rem]" />
        </button>
      </div>
    </div>
  );
};

export { MarketSection };
